import { FormEvent, useState, useSyncExternalStore } from 'react';
import { Alert, Box, Button, Card, CardContent, Checkbox, FormControlLabel, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { apiErrorMessage, login } from '../api/client';
import { getRuntimeConfig } from '../runtime';
import { BrandLogo } from '../brand';
import { availableLocales, getLocale, setLocale, subscribeLocale, t } from '../i18n';
import { requestPasswordReset, resetPassword } from '../features/shell/api';

type View = 'login' | 'forgot' | 'reset';

/** Reset token from `?reset=<token>` (legacy) or `/reset-password/<token>`. */
function readResetToken() {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('reset') ?? params.get('token');
  if (fromQuery) return fromQuery;
  const match = window.location.pathname.match(/reset-password\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

export function LoginPage({
  initialMessage,
  onAuthenticated
}: {
  initialMessage: string | null;
  onAuthenticated: () => Promise<void>;
}) {
  const runtime = getRuntimeConfig();
  const locale = useSyncExternalStore(subscribeLocale, getLocale, getLocale);
  const [resetToken] = useState(readResetToken);
  const [view, setView] = useState<View>(resetToken ? 'reset' : 'login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get('email') ?? '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; sev: 'error' | 'success' } | null>(initialMessage ? { text: initialMessage, sev: 'error' } : null);

  const switchView = (next: View) => {
    setMessage(null);
    setView(next);
  };

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const response = await login(username, password, remember);
      if (!response?.success) {
        const key = response?.lang_key ? `api.${response.lang_key}` : null;
        setMessage({ text: response?.message ?? (key ? t(key) : t('login.failed')), sev: 'error' });
        return;
      }
      await onAuthenticated();
    } catch (error) {
      setMessage({ text: apiErrorMessage(error, t('login.failed')), sev: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const response = await requestPasswordReset(email);
      setMessage({ text: response?.message || t('login.resetLinkSent'), sev: 'success' });
    } catch (error) {
      setMessage({ text: apiErrorMessage(error, t('login.resetFailed')), sev: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (newPassword !== confirmation) {
      setMessage({ text: t('shell.password.mismatch'), sev: 'error' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const response = await resetPassword({ token: resetToken, email, password: newPassword, password_confirmation: confirmation });
      // Clear the token from the URL and go back to the sign-in form.
      window.history.replaceState(null, '', `${runtime.basePath.replace(/\/$/, '')}/`);
      setView('login');
      setMessage({ text: response?.message || t('login.resetDone'), sev: 'success' });
    } catch (error) {
      setMessage({ text: apiErrorMessage(error, t('login.resetFailed')), sev: 'error' });
    } finally {
      setLoading(false);
    }
  }

  const title = view === 'login' ? t('login.welcome', { subtitle: runtime.appSubtitle }) : view === 'forgot' ? t('login.forgotTitle') : t('login.resetTitle');

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 3 }}>
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent>
          <Stack spacing={2.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack spacing={1} alignItems="flex-start">
                <BrandLogo variant="horizontal" height={40} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {runtime.appName}
                </Typography>
                {runtime.tenantName ? (
                  <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700, mt: -1 }}>
                    {runtime.tenantName}
                  </Typography>
                ) : null}
              </Stack>
              <TextField
                select
                size="small"
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                inputProps={{ 'aria-label': t('shell.language') }}
                sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' } }}
              >
                {availableLocales.map((l) => (
                  <MenuItem key={l.code} value={l.code}>
                    {l.flag} {l.code.toUpperCase()}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Typography color="text.secondary">{title}</Typography>
            {message ? <Alert severity={message.sev}>{message.text}</Alert> : null}

            {view === 'login' ? (
              <Stack spacing={2.5} component="form" onSubmit={handleLogin}>
                <TextField
                  autoFocus
                  name="username"
                  label={t('login.username')}
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  required
                  fullWidth
                  autoComplete="username"
                />
                <TextField
                  name="password"
                  label={t('login.password')}
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  fullWidth
                  autoComplete="current-password"
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <FormControlLabel control={<Checkbox checked={remember} onChange={(event) => setRemember(event.target.checked)} />} label={t('login.remember')} />
                  <Link component="button" type="button" variant="body2" onClick={() => switchView('forgot')}>
                    {t('login.forgotLink')}
                  </Link>
                </Stack>
                <Button type="submit" variant="contained" disabled={loading || !username || !password} fullWidth>
                  {loading ? t('login.signingIn') : t('login.signIn')}
                </Button>
              </Stack>
            ) : null}

            {view === 'forgot' ? (
              <Stack spacing={2.5} component="form" onSubmit={handleForgot}>
                <Typography variant="body2" color="text.secondary">
                  {t('login.forgotHelp')}
                </Typography>
                <TextField autoFocus type="email" label={t('login.email')} value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth autoComplete="email" />
                <Button type="submit" variant="contained" disabled={loading || !email} fullWidth>
                  {loading ? t('action.saving') : t('login.sendResetLink')}
                </Button>
                <Link component="button" type="button" variant="body2" onClick={() => switchView('login')}>
                  {t('login.backToLogin')}
                </Link>
              </Stack>
            ) : null}

            {view === 'reset' ? (
              <Stack spacing={2.5} component="form" onSubmit={handleReset}>
                <TextField autoFocus type="email" label={t('login.email')} value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth autoComplete="email" />
                <TextField
                  type="password"
                  label={t('shell.password.new')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="new-password"
                  helperText={newPassword && newPassword.length < 8 ? t('shell.password.minLength', { min: 8 }) : undefined}
                  error={Boolean(newPassword) && newPassword.length < 8}
                />
                <TextField
                  type="password"
                  label={t('shell.password.confirm')}
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  required
                  fullWidth
                  autoComplete="new-password"
                  error={Boolean(confirmation) && confirmation !== newPassword}
                  helperText={confirmation && confirmation !== newPassword ? t('shell.password.mismatch') : undefined}
                />
                <Button type="submit" variant="contained" disabled={loading || !email || newPassword.length < 8 || newPassword !== confirmation} fullWidth>
                  {loading ? t('action.saving') : t('login.resetTitle')}
                </Button>
                <Link component="button" type="button" variant="body2" onClick={() => switchView('login')}>
                  {t('login.backToLogin')}
                </Link>
              </Stack>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
